import { getValidationLogs } from '@services/dashboardService'
import { useEffect, useState, useMemo } from 'react';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { Button } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from 'react-router-dom';
import { showErrorSnackbar } from '@components/shared-layouts/Snackbar/helper';

interface IValidationLog {
  fileName: string;
  status: "Success" | "Failed";
  sliceRange: string;
  error?: string;
}

function ValidationLogsPage() {
  const { projectId } = useParams()
  
  const [validationLogs, setValidationLogs] = useState<IValidationLog[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const columns = useMemo<MRT_ColumnDef<IValidationLog>[]>(
    () => [
      {
        accessorKey: 'fileName',
        header: 'File Name',
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: true,
        size: 200,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: true,
        size: 120,
        Cell: ({ cell }: any) => {
          const status = cell.getValue();
          if (status === 'Success') {
            return (
              <Button 
                variant="contained" 
                color="success" 
                startIcon={<DoneIcon />}
                size="small"
              >
                Success
              </Button>
            );
          } else {
            return (
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<CloseIcon />}
                size="small"
              >
                Failed
              </Button>
            );
          }
        },
      },
      {
        accessorKey: 'sliceRange',
        header: 'Slice Range',
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 150,
        Cell: ({ cell }: any) => {
          const sliceRange = cell.getValue();
          return <span>{sliceRange || '-'}</span>;
        },
      },
      {
        accessorKey: 'error',
        header: 'Error',
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 300,
        Cell: ({ cell, row }: any) => {
          const error = cell.getValue();
          const status = row.getValue('status');
          if (status === 'Failed' && error) {
            return <span style={{ color: '#d32f2f' }}>{error}</span>;
          }
          return <span>-</span>;
        },
      },
    ],
    [],
  );

  const formatValidationLogs = (logsData: any) => {
    let _logs: IValidationLog[] = [];
    const logs = logsData?.logs;

    if (logs && typeof logs === 'object') {
      Object.keys(logs).forEach((fileName) => {
        const logEntry = logs[fileName];
        const item: IValidationLog = {
          fileName: fileName,
          status: logEntry.status || 'Failed',
          sliceRange: logEntry.slice_range || '-',
          error: logEntry.error,
        };
        _logs.push(item);
      });
    }

    setValidationLogs(_logs);
    setLoading(false);
  };

  useEffect(() => {
    if (projectId) {
      setLoading(true);
      getValidationLogs(projectId)
        .then((logsData: any) => {
          formatValidationLogs(logsData);
        })
        .catch((e: any) => {
          console.error('Error fetching validation logs:', e);
          showErrorSnackbar(e?.error?.message || 'Failed to fetch validation logs');
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return (
    <>
      {validationLogs && (
        <MaterialReactTable
          displayColumnDefOptions={{
            'mrt-row-actions': {
              muiTableHeadCellProps: {
                align: 'center',
              },
            },
          }}
          muiTablePaperProps={{ style: { width: '100%', textTransform: 'capitalize' } }}
          columns={columns}
          data={validationLogs}
          enableColumnOrdering
          positionActionsColumn="last"
          state={{ isLoading: loading }}
        />
      )}
    </>
  );
}

export default ValidationLogsPage;

